import React, { Component, ReactNode } from 'react';
import { useWorkspaceErrorSafe } from '@/contexts/WorkspaceErrorContext';
import { parseAzureError } from '@/utils/errors/parseAzureError';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?:
    | ReactNode
    | ((error: Error, errorInfo: React.ErrorInfo) => ReactNode);
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  workspaceId?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

// Internal class component for error boundary (React requires class components for error boundaries)
class ErrorBoundaryClass extends Component<
  ErrorBoundaryProps & { addError?: (error: Error, errorInfo?: React.ErrorInfo, workspaceId?: string, response?: unknown) => void },
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps & { addError?: (error: Error, errorInfo?: React.ErrorInfo, workspaceId?: string, response?: unknown) => void }) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    
    // Extract response data from error if available
    // Some error objects may have response/body properties attached
    let response: unknown = undefined;
    try {
      // Check if error has response data attached
      if ((error as any)?.response) {
        response = (error as any).response;
      } else if ((error as any)?.body) {
        response = (error as any).body;
      } else if (error.message) {
        // Try to parse Azure errors from the error message
        const azureError = parseAzureError(error);
        if (azureError.isAzureError) {
          // Store the parsed Azure error info as response-like data
          response = {
            azureError: azureError,
            message: error.message,
          };
        }
      }
    } catch {
      // If extraction fails, continue without response
    }
    
    // Add error to workspace error context if available
    // The context will parse Azure errors from error message, stack, or response
    if (this.props.addError) {
      this.props.addError(error, errorInfo, this.props.workspaceId, response);
    }
    
    // Call custom onError handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    
    console.error('Error caught by ErrorBoundary:', error);
    console.error('Component stack:', errorInfo.componentStack);
    if (response) {
      console.error('Response data:', response);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        if (typeof this.props.fallback === 'function') {
          return this.props.fallback(
            this.state.error as Error,
            this.state.errorInfo as React.ErrorInfo
          );
        }
        return this.props.fallback;
      }

      return (
        <div
          style={{
            padding: '16px',
            backgroundColor: '#fff3f3',
            border: '1px solid #f56565',
            borderRadius: '4px',
            margin: '16px 0',
          }}
        >
          <h3 style={{ color: '#c53030', marginTop: 0 }}>
            Something went wrong
          </h3>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            <summary>View error details</summary>
            <p>{this.state.error?.toString()}</p>
            <p style={{ fontSize: '12px', fontFamily: 'monospace' }}>
              {this.state.errorInfo?.componentStack}
            </p>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

// Functional component wrapper
export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  fallback,
  onError,
  workspaceId,
}) => {
  // Get the workspace error context safely (returns undefined if not available)
  const errorContext = useWorkspaceErrorSafe();
  const addError = errorContext?.addError;

  return (
    <ErrorBoundaryClass
      fallback={fallback}
      onError={onError}
      workspaceId={workspaceId}
      addError={addError}
    >
      {children}
    </ErrorBoundaryClass>
  );
};

export default ErrorBoundary;
